import '../styles.css';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import { ArrowIcon } from '../icons/navigationIcons';
import { storageAssets } from '../storage_assets';
import IterationStatus from '../components/iterationStatus';
import LazyVideo from '../components/LazyVideo';
import { openExternal } from '../utils/openExternal';

/* Typography: see styles.css — .text-header-main, .text-body, .text-body-sm */

const ProjectNinePage = () => {
    return (
        <div className="scrollable-window scrollable-window--wide">
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader
                        title="Inspiring incremental travel"
                        action={
                            <Button
                                variant="text"
                                text="Visit Breeze"
                                icon={<ArrowIcon />}
                                iconPosition="end"
                                onClick={() => openExternal('https://www.flybreeze.com/home')}
                            />
                        }
                    />

                    <div className="text-body-sm">
                        Breeze Airways recently launched their loyalty program in January of 2026. I contributed to many of the fainl designs that were lanuched. I spring of 2025, I designed
                        the loyalty dashboard and tier education as a UI/UX design intern. I worked mostly solo on this project
                        and pushed lo-fi Figma mockups in front of a PM and marketing team 2–3 times a week. Getting
                        eyes on work fast was deliberate: the quickest way to catch a bad direction is to show it to
                        someone before you&apos;ve committed to it.
                    </div>
                    <div style={styles.dividerStroke}></div>

                    <div className="text-body">The goals</div>
                    <div className="text-body-sm">
                        Two goals shaped every decision on this project. The primary goal: inspire incremental travel by
                        making current benefits immediately visible and motivating users to pursue the next tier. The
                        secondary goal: nudge members toward the Breeze co-brand credit card if they don&apos;t already
                        have one—which meant the card CTA needed a prominent, unconcealed place on the dashboard, not
                        buried.
                    </div>

                    <div className="text-body">Key insight</div>
                    <div className="text-body-sm">
                        Users don&apos;t care about loyalty mechanics—points, tiers, multipliers. They care about three
                        things: <em>What do I get right now? What&apos;s next? How close am I?</em> This reframed the
                        entire experience from informational to motivational.
                    </div>

                    <div className="text-body">1. From structure → motivation</div>
                    <div className="text-body-sm">
                        I started with a competitive analysis. Other loyalty programs use a tabbed, tier-by-tier
                        layout—one tab per tier, showing current benefits on your tab and what future tiers offer on the
                        ones above. It&apos;s a reasonable first answer to the problem of showing now and next at once.
                        But in practice, it buries value inside navigation and drives no action. You&apos;d have to go
                        looking to understand what you already have.
                    </div>
                    <img
                        src={storageAssets.breezeEarlyTabbed}
                        alt="Early lo-fi tabbed tier-based dashboard"
                        style={styles.image}
                    />
                    <IterationStatus items={[
                        { type: 'error', text: 'Layout organized around tiers, not around what the user actually wants to know' },
                        { type: 'error', text: 'Benefits buried inside tabs—value not visible at a glance' },
                        { type: 'error', text: 'No clear next action; nothing motivating the user to do anything' },
                    ]} />
                    <div className="text-body-sm">
                        The shift: make current benefits and next-tier progress front and center. Structure the page
                        around motivation, not around tier mechanics the user doesn&apos;t care about.
                    </div>

                    <div className="text-body">2. Designing for clear states (have vs. choose)</div>
                    <div className="text-body-sm">
                        Alongside fixed benefits, members receive <em>choice benefits</em>—a slot where they pick one
                        option from several to activate as a new perk. Early designs surfaced these in their own
                        separate card on the dashboard, styled identically to regular benefits. From a glance it looked
                        like two blocks of benefit options, with nothing to signal that one block was a menu rather than
                        a list of things you already had.
                    </div>
                    <img
                        src={storageAssets.breezeBadBenefits}
                        alt="Early design: choice benefits indistinguishable from regular benefits"
                        style={styles.image}
                    />
                    <IterationStatus items={[
                        { type: 'error', text: 'Choice benefit card looked identical to active benefits—no reason to interact with it' },
                        { type: 'error', text: 'Nothing communicated that an unclaimed perk was waiting for them' },
                    ]} />
                    <div className="text-body-sm">
                        There was also an edge case: some tiers grant more than one choice benefit. Rather than a
                        separate UI pattern for that scenario, I made the choice benefit card addable—when a member has
                        two to claim, a second dashed card simply appears below the first.
                    </div>
                    <div className="text-body-sm">
                        The final solution stacks all active benefits as cards, then places the choice benefit at the
                        bottom in the same card format—but with a dashed outline and a CTA instead of benefit details.
                        The matching structure signals parity of value with everything above it. The dashed style and
                        the call to action make clear it&apos;s not yet active: it&apos;s waiting for the user to claim
                        it. Tapping opens a modal to browse options and select one.
                    </div>
                    <img
                        src={storageAssets.breezeFinalDashboard}
                        alt="Final benefits stack with dashed choice benefit card"
                        style={styles.image}
                    />
                    <IterationStatus items={[
                        { type: 'success', text: 'Dashed outline and CTA instantly signal: this is yours to claim, not already active' },
                        { type: 'success', text: 'Matching card format communicates parity of value with active benefits' },
                        { type: 'success', text: 'Addable card handles the multi-choice-benefit edge case without a separate pattern' },
                    ]} />

                    <div className="text-body">3. Making value instantly understandable</div>
                    <div className="text-body-sm">
                        Inside the modal, each option has a name—but names alone don&apos;t always communicate value.
                        Take &ldquo;+1 Bundle Upgrade&rdquo;: to understand it fully, a user has to parse that
                        it&apos;s a single-use coupon that upgrades their seat on one flight, adding legroom and extra
                        bags. That&apos;s a lot to infer from a label.
                    </div>
                    <div className="text-body-sm">
                        To close that gap, I designed a Lottie animation for each option. For the Bundle Upgrade, two
                        plain grey airplane seats sit side by side—then the middle seat transforms: it turns pink, the
                        gap between it and the seat ahead visibly widens. In a couple of seconds, the animation explains
                        the upgrade without a word of copy. The same approach scales to every option in the modal,
                        letting users scan and decide quickly rather than reading their way through descriptions.
                    </div>
                    <div style={styles.imageBox}>
                        <LazyVideo
                            src={storageAssets.breezeMobileAnimation}
                            alt="Choice benefit modal with Lottie animations per option on mobile"
                            style={styles.video}
                        />
                    </div>

                    <div className="text-body">4. Benefit card interactions</div>
                    <div className="text-body-sm">
                        How a user gets detail from a benefit card differs by platform. On desktop, hovering a card
                        expands it in place to reveal a fuller description—no navigation, no modal, just the card
                        growing to show more. On mobile, tapping opens a bottom sheet that pulls up from the bottom of
                        the screen. Same information, adapted to how each surface works.
                    </div>

                    <div className="text-body">5. Designing for motivation (earn tracker)</div>
                    <div className="text-body-sm">
                        Early concept: a cockpit-style dial showing point totals. Visually interesting, but showing
                        points is contrary to the job-to-be-done. Users don&apos;t find a point count meaningful—they
                        want to know how far they are and what they&apos;re missing to get to the next tier.
                    </div>
                    <div style={styles.dialPairRow}>
                        <div style={styles.dialPairRowItem}>
                        <img
                            src={storageAssets.breezeEarlyDialOne}
                            alt="Early dial-style tracker showing points (first)"
                            style={{ ...styles.dialImage, width: '85%' }}
                        />
                        </div>
                        <img
                            src={storageAssets.breezeEarlyDialTwo}
                            alt="Early dial-style tracker showing points (second)"
                            style={styles.dialImage}
                        />
                    </div>
                    <IterationStatus items={[
                        { type: 'error', text: 'Dial centered on point totals—a metric users don\'t find meaningful' },
                        { type: 'error', text: 'No sense of how far away the next tier was; progress wasn\'t visible' },
                    ]} />
                    <div className="text-body-sm">
                        The next iteration replaced the point total with the tier label as the primary metric and
                        switched to monochrome per-tier colors. Marketing wanted each tier distinctly branded, and
                        monochrome also made Lottie animations viable without color conflicts.
                    </div>
                    <div className="text-body-sm">
                        Getting users to notice the tracker on load was the next problem. I tried a looping cloud
                        animation in the background. It drew eyes immediately—but in the wrong way. The perpetual
                        motion competed with the fill bar rather than directing attention to it, pulling focus away from
                        the one thing I needed users to read first.
                    </div>
                    <div className="text-body-sm">
                        The final version plays a one-time animation on first load and refresh only: an airplane-window
                        view with clouds drifting past and a wing visible in frame, slowing to a stop in sync with the
                        progress bar filling to the user&apos;s current tier progress. Once it stops, the dashboard is
                        still. Users see their progress land without anything that keeps demanding their attention. It
                        leverages the goal-gradient effect—the closer you are to the finish line, the more motivated you
                        feel—and answers &ldquo;how close am I?&rdquo; before they&apos;ve read a single word.
                    </div>
                    <div style={styles.imageBox}>
                        <LazyVideo
                            src={storageAssets.breezeFinalEarnTracker}
                            alt="Final earn tracker with progress bar and airplane window"
                            style={styles.video}
                        />
                    </div>
                    <IterationStatus items={[
                        { type: 'success', text: 'Progress bar answers "how close am I?" without needing to interpret a number' },
                        { type: 'success', text: 'One-time animation draws attention on load without staying disruptive' },
                        { type: 'success', text: 'Goal-gradient effect: visible proximity to the next tier motivates action' },
                    ]} />

                    <div className="text-body">6. A shared visual language</div>
                    <div className="text-body-sm">
                        The airplane window in the earn tracker isn&apos;t unique to that component. In the tier
                        education section—where users can browse each tier and its associated benefits—each tier is
                        represented by the same airplane window shape, rendered in that tier&apos;s color. Seeing the
                        same form in both places establishes a mental model: the window means &ldquo;your tier,&rdquo;
                        and the color tells you which one. The earn tracker and the tier education section reinforce
                        each other instead of feeling like separate features.
                    </div>

                    <div className="text-body">Outcome</div>
                    <div className="text-body-sm">
                        Several months after I left Breeze, the loyalty progam lanuched and I was happy to see that a number of the designs shown here were used in the final product.
                    </div>

                    <div className="text-body">What I learned</div>
                    <div className="text-body-sm">
                        Design for behavior, not information. Two things from this project stuck with me. First:
                        clarity of state matters more than clarity of structure—when users can&apos;t tell what&apos;s
                        active versus available, nothing feels actionable, no matter how well-organized the layout is.
                        Second: animation that plays once and stops is often stronger than animation that loops. Looping
                        earns attention by demanding it indefinitely. A one-shot animation earns it once, then gets out
                        of the way.
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        maxWidth: '100%',
        minWidth: 0,
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    dividerStroke: {
        height: '1px',
        backgroundColor: 'var(--color-border)',
        alignSelf: 'stretch',
    },
    imageBox: {
        display: 'flex',
        flexDirection: 'row',
        gap: '12px',
        width: '100%',
        justifyContent: 'center',
        paddingTop: '20px',
        paddingBottom: '40px',
    },
    image: {
        width: 'var(--content-media-width)',
        height: 'auto',
        maxWidth: '100%',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        display: 'block',
    },
    video: {
        width: 'clamp(100px, 47%, 800px)',
        height: 'auto',
        maxWidth: '100%',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        display: 'block',
    },
    dialPairRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '12px',
        width: '100%',
        alignSelf: 'stretch',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20px',
        paddingBottom: '40px',
    },
    dialImage: {
        width: 'clamp(100px, 47%, 800px)',
        height: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
    },
    metaRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '24px',
        alignSelf: 'stretch',
    },
    metaItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    metaLabel: {
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--color-text-secondary)',
    },
    dialPairRowItem: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'stretch',
        width: 'clamp(100px, 47%, 800px)',
        maxWidth: '100%',
        borderRadius: '20px',
        border: '1px solid #B9DEFF',
        backgroundColor: '#FFFFFF',
    },
};

export default ProjectNinePage;
